<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12">
        <div class="d-flex flex-wrap">
          <div class="d-flex align-center">
            <h2>
              {{ $t("message.reviews.reviews") }}
            </h2>
            <div class="ml-4">
              <v-tooltip bottom :max-width="500">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-icon color="lime" size="18">
                      mdi-information
                    </v-icon>
                  </div>
                </template>
                {{ $t("message.reviews.tip") }}
              </v-tooltip>
            </div>
          </div>
          <div class="d-flex flex-grow-1 justify-end align-center">
            <div class="d-flex align-center flex-wrap">
              <ReviewAiLimitButton
                v-if="canUseAiReviewReply && canReplyToReviewsManually"
                :ai-limits="aiLimits"
                :can-configure-ai-reply="isAiConfigurationDialogAvailable"
                class="mr-3"
                @open-ai-settings-dialog="openAiSettingsDialogVisible = true"
              />

              <ui-button
                v-if="canManageTags"
                @click="openDialog('tagsManager')"
                outlined
                icon="icon-tags"
                class="mr-3"
              >
                {{ $t("message.reviews.tags.manageTagsBasic") }}
              </ui-button>
              <ui-button
                v-if="
                  isFeatureFlagEnabled(
                    FEATURE_FLAG.REVIEWS_SHOW_REPLY_TEMPLATES
                  ) &&
                    canUseReviewTemplates &&
                    canReplyToReviewsManually
                "
                @click="openDialog('replyTemplatesCategories')"
                outlined
                icon="icon-template-add"
                class="mr-3"
              >
                {{ $t("message.reviews.replyTemplates.title") }}
              </ui-button>
              <template v-if="selectedTab === 0">
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  :return-value.sync="date"
                  transition="scale-transition"
                  offset-y
                  :close-on-click="false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      class="min-w-230 calendar-input"
                      :value="dateComputed"
                      :label="$t('message.reviews.dateRange')"
                      prepend-icon="icon-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="date"
                    first-day-of-week="1"
                    :locale="selectedLocale"
                    color="primary"
                    no-title
                    scrollable
                    range
                    class="pa-2"
                  >
                    <div class="full-width">
                      <div class="dates-select">
                        <v-text-field
                          v-model="dateFrom"
                          @keyup.enter="handleEditDatesBtn"
                          :label="$t('message.common.dateFrom')"
                          :disabled="!editDates"
                        ></v-text-field
                        ><v-text-field
                          v-model="dateTo"
                          @keyup.enter="handleEditDatesBtn"
                          :label="$t('message.common.dateTo')"
                          :disabled="!editDates"
                        ></v-text-field>
                        <v-btn icon color="primary" @click="handleEditDatesBtn">
                          <v-icon>
                            {{ editDates ? "icon-check" : "icon-edit" }}
                          </v-icon>
                        </v-btn>
                      </div>
                      <div class="d-flex justify-space-between">
                        <ui-button
                          outlined
                          transparent
                          color="primary"
                          @click="closeMenu"
                          >{{ $t("message.reviews.cancel") }}
                        </ui-button>
                        <v-spacer></v-spacer>
                        <ui-button
                          outlined
                          transparent
                          color="primary"
                          @click="handleDate()"
                          >{{ $t("message.common.choose") }}
                        </ui-button>
                      </div>
                    </div>
                  </v-date-picker>
                </v-menu>
                <ui-button
                  color="primary"
                  class="ml-6"
                  icon="icon-filter"
                  @click="openChildDialog('filter')"
                >
                  {{ $t("message.reviews.filter") }}
                </ui-button>
                <ui-button
                  :loading="loadingExport"
                  color="primary"
                  class="ml-3"
                  icon="icon-download"
                  outlined
                  @click="exportToCsv"
                >
                  {{ $t("message.common.exportToFile") }}
                </ui-button>
              </template>
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-tabs v-model="selectedTab" slider-color="primary" class="tabs">
            <v-tab
              v-for="tab in visibleTabs"
              :key="tab.id"
              :ripple="false"
              class="tabs__item"
            >
              {{ tab.text }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="selectedTab" class="tabs__content">
            <v-tab-item>
              <BaseReviewsList
                v-if="selectedTab === 0"
                ref="baseReviewsList"
                :initial-date="date"
                @open-dialog="openDialog"
                @export="handleExport"
              />
            </v-tab-item>
            <v-tab-item v-if="showAutoReplyTab">
              <AutoReplyReviewsList
                v-if="selectedTab === 1"
                ref="autoReplyReviewsList"
                @open-dialog="openDialog"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>
    <TagsManagerDialog
      v-if="dialog.tagsManager"
      :is-open="dialog.tagsManager"
      :tags="tags"
      :is-tag-loading="isLoading"
      @close="dialog.tagsManager = false"
      @list-update="handleListUpdate"
    />
    <ReplyTemplatesCategoriesDialog
      v-if="canUseReviewTemplates"
      :is-open="dialog.replyTemplatesCategories"
      @close="dialog.replyTemplatesCategories = false"
    />
    <AsyncDownloadDialog
      v-if="showAsyncDownloadDialog"
      :export-id="exportId"
      @close="showAsyncDownloadDialog = false"
    />
    <AiSettingsDialog
      v-if="openAiSettingsDialogVisible && canUseAiReviewReply"
      @close="openAiSettingsDialogVisible = false"
    />
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";

import { FORMAT } from "@/config/date-format";
import serviceHandler from "@/helpers/handlers/service";
import {
  gtmEvent__filterDatePeriod,
  gtmEvent__utmEmailNotification
} from "@/helpers/gtm-events";
import { isUtmTaggedUrl } from "@/helpers/utm-tags";
import tagsService from "@/services/tags";
import { PERIOD } from "@/enums/period.enum";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { setCompanyIdFromQuery as setCompanyIdFromQueryMixin } from "@/helpers/mixins/set-company-id-from-query";
import reviewsService from "@/services/reviews";
import ReplyTemplatesCategoriesDialog from "@/components/Reviews/ReplyTemplates/CategoriesDialog";
import BaseReviewsList from "./BaseReviewsList.vue";
import AutoReplyReviewsList from "./AutoReplyReviewsList.vue";

import ReviewAiLimitButton from "@/components/Reviews/ReviewAiLimitButton.vue";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";

export default {
  name: "ReviewsList",
  components: {
    TagsManagerDialog: () => import("@/components/Tags/TagsManagerDialog"),
    ReplyTemplatesCategoriesDialog,
    ReviewAiLimitButton,
    BaseReviewsList,
    AutoReplyReviewsList,
    AsyncDownloadDialog: () => import("@/components/AsyncDownloadDialog"),
    AiSettingsDialog: () => import("@/components/Reviews/AiSettingsDialog")
  },
  mixins: [permissionsInfo, gtmEngagementMixin, setCompanyIdFromQueryMixin],
  provide: {
    showReviewAddedWithRb: isFeatureFlagEnabled(
      FEATURE_FLAG.SHOW_REVIEW_ADDED_WITH_RB
    ),
    showReplyTemplates: isFeatureFlagEnabled(
      FEATURE_FLAG.REVIEWS_SHOW_REPLY_TEMPLATES
    )
  },
  data() {
    return {
      FEATURE_FLAG,
      menu: false,
      dialog: {
        replyTemplatesCategories: false,
        tagsManager: false,
        tagsPicker: false
      },
      date: this.getStartDate(),
      dateFormatted: null,
      dateFrom: moment()
        .subtract(1, PERIOD.MONTH)
        .add(1, PERIOD.DAY)
        .format(FORMAT.DATE),
      dateTo: moment().format(FORMAT.DATE),
      editDates: false,
      dateInputType: null,
      isLoading: false,
      tags: [],
      showAsyncDownloadDialog: false,
      exportId: null,
      loadingExport: false,
      aiLimits: { remaining_limit: 0, monthly_limit: 0 },
      openAiSettingsDialogVisible: false,
      selectedTab: 0,
      tabs: [
        { id: 1, text: this.$t("message.reviews.reviewsList") },
        {
          id: 2,
          text: this.$t("message.reviews.reviewsAIReplyList"),
          featureFlag: FEATURE_FLAG.CN_6665_AUTO_REVIEW_REPLY
        }
      ]
    };
  },

  async created() {
    if (this.$route.query.date_from && this.$route.query.date_to) {
      this.dateFrom = this.$route.query.date_from;
      this.dateTo = this.$route.query.date_to;
    }
    if (this.$route.query.date_from && !this.$route.query.date_to) {
      this.dateFrom = this.$route.query.date_from;
      this.dateTo = moment().format(FORMAT.DATE);
    }

    if (this.canReplyReviews) {
      await this.getAiLimit();
    }
  },

  mounted() {
    this.fetchTags();

    if (isUtmTaggedUrl(this.$route.query)) {
      gtmEvent__utmEmailNotification(this.$route.query);
    }
    this.mixin__setGtmEngagementTimeouts();
  },

  computed: {
    canReplyToReviewsManually() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_REPLY_TO_REVIEWS_MANUALLY
      );
    },
    canUseAiReviewReply() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_AI_REVIEW_REPLY);
    },
    canUseReviewTemplates() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_REVIEW_TEMPLATES);
    },
    ...mapState({
      loading: state => state.reviews.loading,
      selectedLocale: state => state.settings.selectedLocale
    }),
    isAiConfigurationDialogAvailable() {
      return this.canConfigureAiReviewReply;
    },
    dateComputed() {
      if (this.date && this.date[0] && this.date[1]) {
        return `${moment(this.date[0]).format(FORMAT.DATE)} - ${moment(
          this.date[1]
        ).format(FORMAT.DATE)}`;
      }
      return "";
    },
    showAutoReplyTab() {
      return isFeatureFlagEnabled(FEATURE_FLAG.CN_6665_AUTO_REVIEW_REPLY);
    },
    visibleTabs() {
      return this.tabs.filter(tab => {
        if (tab.featureFlag) {
          return isFeatureFlagEnabled(tab.featureFlag);
        }
        return true;
      });
    }
  },

  methods: {
    async openAiSettingsDialog() {
      this.openAiSettingsDialogVisible = true;
    },
    async onSentimentChange(sentiment) {
      await reviewsService.patchReviewCategory(sentiment);
    },
    async getAiLimit() {
      if (!this.canUseAiReviewReply) {
        return;
      }
      this.aiLimits = await serviceHandler(reviewsService.getAiLimit());
    },
    openChildDialog(dialogName) {
      this.$refs.baseReviewsList.openDialog(dialogName);
    },
    async exportToCsv() {
      this.loadingExport = true;
      this.$refs.baseReviewsList.exportToCsv();
    },
    handleExport(export_id) {
      this.exportId = export_id;
      this.showAsyncDownloadDialog = true;
      this.loadingExport = false;
    },
    async handleListUpdate(event) {
      this.$refs.baseReviewsList.handleListUpdate(event);
    },
    isFeatureFlagEnabled,
    getStartDate() {
      if (this.$route.query.date_from && this.$route.query.date_to) {
        return [this.$route.query.date_from, this.$route.query.date_to];
      }
      if (this.$route.query.date_from && !this.$route.query.date_to) {
        return [this.$route.query.date_from, moment().format(FORMAT.DATE)];
      }

      return [
        moment()
          .subtract(1, PERIOD.MONTH)
          .add(1, PERIOD.DAY)
          .format(FORMAT.DATE),
        moment().format(FORMAT.DATE)
      ];
    },
    goToGMBAccounts() {
      this.$router.push({ name: "profile-gmb-accounts" });
    },
    openDialog(type) {
      this.dialog[type] = true;
    },
    handleDate() {
      if (this.date[1]) {
        if (!moment(this.date[0]).isBefore(this.date[1])) {
          this.date = [this.date[1], this.date[0]];
        }
      } else {
        this.date = [this.date[0], this.date[0]];
      }

      this.$refs.baseReviewsList.handleDate(this.date);

      gtmEvent__filterDatePeriod({
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        dateInputType: this.dateInputType
      });

      this.$refs.menu.save(this.date);
    },

    closeMenu() {
      this.menu = false;
    },

    handleEditDatesBtn() {
      if (this.editDates) {
        this.setDates();
      }
      this.editDates = !this.editDates;
    },

    async setDates() {
      const oldDate = this.date;

      if (!moment(this.dateFrom).isValid()) {
        this.dateFrom = oldDate[0];
      }
      if (!moment(this.dateTo).isValid()) {
        this.dateTo = oldDate[1];
      }
      this.date = [this.dateFrom, this.dateTo];

      await this.$nextTick(() => {
        this.dateInputType = "manual";
      });
    },

    async fetchTags() {
      if (!this.canTagReviews) {
        return;
      }
      this.isLoading = true;
      this.tags = await serviceHandler(tagsService.getTags());
      this.isLoading = false;
    }
  },

  watch: {
    date(newDate) {
      if (newDate[0] && this.dateFrom !== newDate[0]) {
        this.dateFrom = newDate[0];
      }
      if (newDate[1] && this.dateTo !== newDate[1]) {
        this.dateTo = newDate[1];
      }

      this.dateInputType = "calendar";
    }
  }
};
</script>

<style lang="scss" scoped>
.date-type-select {
  width: 300px;
}

.calendar-input {
  margin-top: $spacing * 1.25;
  margin-bottom: $spacing * -1.25;
}

.dates-select {
  display: flex;
  align-items: center;
  padding: $spacing 0;
  border-top: 1px solid $light-3;
  margin-bottom: $spacing;

  .v-text-field {
    width: 100px;
    margin-bottom: -$spacing * 2;
  }

  & > :not(:last-child) {
    margin-right: $spacing;
  }
}

.min-w-230 {
  min-width: 230px;
}

::v-deep .v-date-picker-table {
  padding: 0;
}

.tabs {
  border-bottom: 1px solid $gray-200;
  padding: 0 $spacing * 3;
}
</style>
